import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import { Moment } from 'moment'

import './AppointmentPicker.sass'

export type Props = {
    patientName: string
    location: Models.PracticeLocation
    procedure: Models.Procedure
    selectedDate: Moment
    isFormValid: boolean
    timeslotError: boolean
    displayDays: number
    selectedDatetime?: Moment
    errorMessage?: string
    onMakeChanges: () => void
    onBookAppointment: () => Promise<void>
    earlierAvailabilityToggleEnabled: boolean
    isLegacyAgent: boolean
    askAboutEarlierAvailabilityForLocation: boolean
    wantsSoonerIfPossible: boolean
    onWantsSoonerIfPossibleChange: (value: boolean) => void
}

export type AppointmentPickerState = {
    startDate: Moment
    endDate: Moment
    minDate: Moment
    isLoading: boolean
}

const moduleName = 'scheduling-appointment-picker'

const AppointmentPickerPreviewOverlay = ({
    patientName,
    selectedDate,
    selectedDatetime,
    displayDays,
    procedure,
    location,
    isFormValid,
    timeslotError,
    onMakeChanges,
    onBookAppointment,
    earlierAvailabilityToggleEnabled,
    isLegacyAgent,
    askAboutEarlierAvailabilityForLocation,
    wantsSoonerIfPossible,
    onWantsSoonerIfPossibleChange,
}: Props) => {
    const showEarlierAvailabilityCheckbox =
        earlierAvailabilityToggleEnabled && !isLegacyAgent && askAboutEarlierAvailabilityForLocation
    return (
        <div className={`${moduleName}__preview-appointment-overlay`}>
            <div className={`${moduleName}__preview-appointment`}>
                <i className={`material-icons ${moduleName}__info-icon`}>info</i>

                <div className={`${moduleName}__preview-appointment-header`}>
                    You are about to book the following appointment:
                </div>
                <div className={`${moduleName}__preview-appointment-sub-header`}>
                    Please confirm the following details:
                </div>

                <div className={`${moduleName}__preview-appointment-body`}>
                    <div className={`${moduleName}__preview-appointment-body-item`}>
                        Patient Name: <span className={`${moduleName}--bold`}>{patientName}</span>
                    </div>
                    <div className={`${moduleName}__preview-appointment-body-item`}>
                        Location: <span className={`${moduleName}--bold`}>{location.name}</span>
                    </div>
                    <div className={`${moduleName}__preview-appointment-body-item`}>
                        Procedure: <span className={`${moduleName}--bold`}>{procedure.name}</span>
                    </div>
                    <div className={`${moduleName}__preview-appointment-body-item`}>
                        Date/Time:{' '}
                        <span className={`${moduleName}--bold`}>
                            {selectedDatetime
                                ?.tz(location.timezone || 'America/New_York')
                                .format('MMMM D, YYYY [at] h:mm A z')}
                        </span>
                    </div>
                </div>

                {showEarlierAvailabilityCheckbox && (
                    <div className={`${moduleName}__earlier-availability`}>
                        <div className={`${moduleName}__earlier-availability-title`}>
                            Would the patient like an earlier appointment if one becomes available?
                        </div>
                        <div className={`${moduleName}__earlier-availability-description`}>
                            If an earlier appointment opens up, we'll reach out to offer it to them.
                        </div>
                        <label className={`${moduleName}__earlier-availability-checkbox-label`}>
                            <Checkbox
                                data-testid="wants-sooner-if-possible-checkbox"
                                checked={wantsSoonerIfPossible}
                                onChange={(e) => onWantsSoonerIfPossibleChange(e.target.checked)}
                                size="small"
                            />
                            Yes, contact patient if an earlier appointment opens up
                        </label>
                    </div>
                )}
            </div>

            <div
                className={`${
                    displayDays !== 3
                        ? `${moduleName}__preview-appointment-action-buttons`
                        : `${moduleName}__preview-appointment-action-buttons-narrow`
                }`}
            >
                <div className={`${moduleName}__submit`}>
                    <Button
                        className={`${
                            displayDays !== 3
                                ? `${moduleName}__make-changes-button`
                                : `${moduleName}__make-changes-button-narrow`
                        }`}
                        onClick={onMakeChanges}
                    >
                        MAKE CHANGES
                    </Button>
                </div>
                <div className={`${moduleName}__submit`}>
                    <Button
                        disabled={Boolean(!selectedDatetime || !isFormValid || timeslotError)}
                        className={`${
                            displayDays !== 3 ? `${moduleName}__submit-button` : `${moduleName}__submit-button-narrow`
                        }`}
                        onClick={onBookAppointment}
                    >
                        SCHEDULE APPOINTMENT
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default AppointmentPickerPreviewOverlay
